import { NotifyFail, NotifySuccess } from '@/helpers/core/notifications'
import {
  getWorkOrderContainers,
  getWorkOrderDashboardSummary,
  updateWorkOrder,
} from '@/services/rc/work-orders.service'
import { useOIDCStore } from '@/stores/oidc.store'
import type { IWorkOrderContainersFilters } from '@/types/rc/filters'
import type { IWorkOrderUpdateForm } from '@/types/rc/forms'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

export const useWorkOrder = (number: string) => {
  const queryClient = useQueryClient()
  const { profile, isRcAppOperator } = storeToRefs(useOIDCStore())

  const workOrderDashboardSummaryQuery = () =>
    useQuery({
      queryKey: ['work-order-dashboard-summary', number],
      queryFn: () => getWorkOrderDashboardSummary(number),
    })

  const workOrderContainersQuery = (filters: IWorkOrderContainersFilters) =>
    useQuery({
      queryKey: ['work-order-containers', number, filters],
      queryFn: () => getWorkOrderContainers(number, filters),
    })

  const updateWorkOrderMutation = useMutation({
    mutationFn: (form: IWorkOrderUpdateForm) => updateWorkOrder(number, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-order-dashboard-summary', number] })
      NotifySuccess('Work Order updated successfully.')
    },
    onError: (e: any) => NotifyFail(e.message),
  })

  const { data, isError, isPending } = workOrderDashboardSummaryQuery()

  const isOutsourced = computed(
    () => !!profile.value && profile.value.tenant_id !== data.value?.work_order_information.tenant_id
  )

  const isCustomer = computed(
    () =>
      !!data.value &&
      !!profile.value &&
      profile.value.customer_ids.includes(data.value.work_order_information.customer.id)
  )

  // Can perform write actions on the WO
  const isManageableByCurrentUser = computed(
    () => !isError.value && !isPending.value && !!profile.value && !isCustomer.value && !isOutsourced.value
  )

  // Can see the billing tab
  const isBillingRelevantForCurrentUser = computed(
    () => !isError.value && !isPending.value && !!profile.value && !isRcAppOperator.value
  )

  return {
    workOrderDashboardSummaryQuery,
    workOrderContainersQuery,
    updateWorkOrderMutation,
    isManageableByCurrentUser,
    isOutsourced,
    isBillingRelevantForCurrentUser,
    isCustomer,
  }
}
